import { useState } from "react";
import BaseModal from "../base/BaseModal";
import BaseButton from "../base/BaseButton";
import { Formik } from "formik";
import BaseInput from "../base/BaseInput";
import { useAppDispatch, useAppSelector } from "../../store";
import { createCustomerProfileAction } from "../../store/slices/customer/action";
import { genderTypes, titles } from "../../data/utils";
import { BasePhoneInput, BaseSelect } from "../base";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Platform } from "../../enums/plaftorm.enum";

const AddProfileModal = ({
  isOpen,
  setOpen,
  accountId,
}: {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  accountId: string;
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [formValue, setFormValue] = useState({
    gender: {
      id: "",
      name: "",
    },
    title: {
      id: "",
      name: "",
    },
    mobile: {
      value: "",
    },
  });

  type ErrorMsg = {
    genderError: string;
    titleError: string;
  };

  const [errorMsg, setErrorMsg] = useState<ErrorMsg>({
    genderError: "",
    titleError: "",
  });

  const { currentOrganisationAccount } = useAppSelector(
    (state) => state.authReducer
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onClose = () => {
    setOpen(false);
    setIsProcessing(false);
    setFormValue((prevState) => ({
      ...prevState,
      gender: {
        id: "",
        name: "",
      },
      title: {
        id: "",
        name: "",
      },
    }));
  };

  const userObj = {
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    email: "",
    mobile: "",
  };

  const validateAddProfile = (values: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  }) => {
    let errors: any = {};
    if (!values.dateOfBirth) {
      errors.dateOfBirth = "Date of birth cannot be blank";
    }

    if (!values.firstName) {
      errors.firstName = "First Name cannot be blank";
    }
    if (!values.lastName) {
      errors.lastName = "Last Name cannot be blank";
    }

    if (!formValue?.gender?.id) {
      errors.gender = "Please select gender";
      setErrorMsg((prevState) => ({
        ...prevState,
        genderError: "Please select gender",
      }));
    }

    if (!formValue?.title?.id) {
      errors.title = "Please select title";
      setErrorMsg((prevState) => ({
        ...prevState,
        titleError: "Please select title",
      }));
    }
    return errors;
  };

  const createCustomerProfile = async (data: {
    firstName: string;
    lastName: string;
    dateOfBirth: string | Date;
    email: string;
  }) => {
    setIsProcessing(true);
    const { firstName, lastName, dateOfBirth, email } = data;

    const payload = {
      firstName,
      lastName,
      dateOfBirth: new Date(dateOfBirth),
      gender: formValue?.gender?.id,
      title: formValue?.title?.name,
      createdByOrganisationId: currentOrganisationAccount?.organisationId || "",
      accountId,
      createdFromPlatform: Platform.OrganisationDashboard,
      email,
      mobile: formValue?.mobile?.value,
    };

    const resp = await dispatch(createCustomerProfileAction(payload));

    if (resp?.payload?.status === 201) {
      toast.success("Customer profile added successfully");
      setIsProcessing(false);
      navigate("/dashboard/customers");
      setOpen(false);
    } else {
      toast.error(resp?.payload?.message);
      setIsProcessing(false);
    }
  };

  const selectGender = (obj: { id: string; name: string }) => {
    setFormValue({ ...formValue, gender: obj });
    setErrorMsg({ ...errorMsg, genderError: "" });
  };

  const selectTitle = (obj: { id: string; name: string }) => {
    setFormValue({ ...formValue, title: obj });
    setErrorMsg({ ...errorMsg, titleError: "" });
  };

  const selectPhone = (obj: any) => {
    setFormValue((prevState) => ({
      ...prevState,
      mobile: obj,
    }));
    setErrorMsg((prevState) => ({
      ...prevState,
      mobileError: "",
    }));
  };

  return (
    <BaseModal
      closeModal={onClose}
      closable={true}
      visible={isOpen}
      modalSize="large"
      styleClass={""}
      disabled={false}
      label={""}
      id={""}
      value={""}
      hasError={false}
      errorMsg={""}
      styles={""}
    >
      <div className="text-center">
        <h2 className=" font-playfair textbrandColor text-[24px] font-medium">
          Add a Profile
        </h2>
        <h3 className=" text-[0.75rem]">
          Add a new profile to customer's account
        </h3>
        <div className="transition my-6 lg:w-11/12 w-full m-auto">
          <Formik
            initialValues={userObj}
            onSubmit={createCustomerProfile}
            validate={validateAddProfile}
          >
            {(formik) => {
              const {
                values,
                touched,
                handleChange,
                handleSubmit,
                errors,
                handleBlur,
              } = formik;
              return (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-2 gap-4">
                    <BaseInput
                      id={"firstName"}
                      value={userObj.firstName}
                      label="First Name"
                      name={"firstName"}
                      type="text"
                      hasError={Boolean(errors.firstName && touched.firstName)}
                      change={handleChange}
                      blur={handleBlur}
                      errorMsg={errors.firstName ? errors.firstName : undefined}
                      bodyStyles="mb-6"
                    />
                    <BaseInput
                      id={"lastName"}
                      value={userObj.lastName}
                      label="Last Name"
                      name={"lastName"}
                      type="text"
                      hasError={Boolean(errors.lastName && touched.lastName)}
                      change={handleChange}
                      blur={handleBlur}
                      errorMsg={errors.lastName ? errors.lastName : undefined}
                      bodyStyles="mb-6"
                    />
                    <BaseSelect
                      value={formValue.title}
                      label="Title"
                      name={"title"}
                      styleClass="mb-6"
                      hasError={!!errorMsg.titleError}
                      errorMsg={errorMsg.titleError}
                      onChange={selectTitle}
                      options={titles}
                      selectKey="name"
                      textClass={""}
                      filter={true}
                      showClear={false}
                      inputId={""}
                    />
                    <BaseSelect
                      value={formValue.gender}
                      label="Gender"
                      name={"gender"}
                      styleClass="mb-6"
                      hasError={!!errorMsg.genderError}
                      errorMsg={errorMsg.genderError}
                      onChange={selectGender}
                      options={genderTypes}
                      selectKey="name"
                      textClass={""}
                      filter={false}
                      showClear={false}
                      inputId={""}
                    />
                    <BaseInput
                      id={"dateOfBirth"}
                      value={values.dateOfBirth as string}
                      label="Date of Birth"
                      name={"dateOfBirth"}
                      type="date"
                      hasError={errors.dateOfBirth && touched.dateOfBirth}
                      change={handleChange}
                      blur={handleBlur}
                      errorMsg={errors.dateOfBirth}
                      bodyStyles="mt-6"
                    />
                    <BaseInput
                      id={"email"}
                      value={values.email}
                      label="Email"
                      name={"email"}
                      type="email"
                      change={handleChange}
                      blur={handleBlur}
                      bodyStyles="mt-6"
                    />
                    <div className="mt-6">
                      <BasePhoneInput
                        value={formValue.mobile?.value}
                        label="Phone Number"
                        id="mobile"
                        blur={handleBlur}
                        selectPhone={selectPhone}
                      />
                    </div>

                    <BaseButton
                      text="Add Profile"
                      type="submit"
                      variant="primary"
                      styles=" w-3/5 mx-auto mt-4 col-span-2"
                      processing={isProcessing}
                    />
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddProfileModal;
